import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { fmtUSD, SERVICE_LABELS } from './utils';
import { TrendingUp, Calendar } from 'lucide-react';
import logger from '../../utils/logger';

function getWeekRange(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + 7 * offset);  // Sunday of this week
  const start = d.toISOString().slice(0, 10);
  const e = new Date(d); e.setDate(e.getDate() + 6);
  return { start, end: e.toISOString().slice(0, 10) };
}
function getMonthRange(offset = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offset, 1);
  const start = d.toISOString().slice(0, 10);
  const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { start, end: e.toISOString().slice(0, 10) };
}

export default function RevenueTab({ api }) {
  const [mode, setMode] = useState('month');
  const [range, setRange] = useState(() => getMonthRange(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/finance/revenue-projections?start=${range.start}&end=${range.end}`);
      setData(data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api, range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setPreset = (m) => {
    setMode(m);
    if (m === 'week') setRange(getWeekRange(0));
    else if (m === 'month') setRange(getMonthRange(0));
    else if (m === 'quarter') {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3);
      const start = new Date(now.getFullYear(), q * 3, 1).toISOString().slice(0, 10);
      const end = new Date(now.getFullYear(), q * 3 + 3, 0).toISOString().slice(0, 10);
      setRange({ start, end });
    } else if (m === 'year') {
      const y = new Date().getFullYear();
      setRange({ start: `${y}-01-01`, end: `${y}-12-31` });
    }
  };

  const totalRevenue = data?.total_revenue || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {[['week','Week'],['month','Month'],['quarter','Quarter'],['year','Year']].map(([k, l]) => (
          <Button key={k} size="sm" variant={mode === k ? 'default' : 'outline'}
            onClick={() => setPreset(k)}
            className={mode === k ? 'bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white h-9' : 'border-[#DED8EB] h-9'}
            data-testid={`revenue-preset-${k}`}>{l}</Button>
        ))}
        <div className="flex items-center gap-2 ml-2">
          <Label className="text-xs text-[#5A4D6E]">From</Label>
          <Input type="date" value={range.start} onChange={e => { setMode('custom'); setRange(r => ({ ...r, start: e.target.value })); }}
            className="h-9 border-[#DED8EB] w-40 text-sm" data-testid="revenue-start" />
          <Label className="text-xs text-[#5A4D6E]">to</Label>
          <Input type="date" value={range.end} onChange={e => { setMode('custom'); setRange(r => ({ ...r, end: e.target.value })); }}
            className="h-9 border-[#DED8EB] w-40 text-sm" data-testid="revenue-end" />
        </div>
      </div>

      <Card className="border-[#DED8EB] bg-gradient-to-br from-[#EDE7F6] to-white shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1 text-[#6B3FA0]">
            <TrendingUp className="h-4 w-4" /> <span className="text-xs font-semibold uppercase tracking-wider">Total Projected Revenue</span>
          </div>
          <p className="text-4xl font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="revenue-total">
            {fmtUSD(totalRevenue)}
          </p>
          <p className="text-xs text-[#8E85A0] mt-1 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {range.start} → {range.end}
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {['day_program', 'employment', 'in_home', 'private_pay'].map(sl => {
          const s = data?.by_service?.[sl];
          const isPP = sl === 'private_pay';
          return (
            <Card key={sl} className="border-[#DED8EB] bg-white shadow-sm" data-testid={`revenue-${sl}`}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#8E85A0] mb-1">{SERVICE_LABELS[sl]}</p>
                <p className="text-2xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtUSD(s?.revenue || 0)}</p>
                <div className="mt-2 text-[11px] text-[#8E85A0] space-y-0.5">
                  {isPP ? (
                    <p><span className="font-semibold text-[#5A4D6E]">{s?.sessions || 0}</span> session{s?.sessions === 1 ? '' : 's'} (per-session pricing)</p>
                  ) : (
                    <>
                      <p><span className="font-semibold text-[#5A4D6E]">{s?.units?.toFixed(1) || 0}</span> units @ {fmtUSD(s?.rate || 0)}/unit</p>
                      <p>{s?.participant_hours?.toFixed(1) || 0} participant-hours</p>
                    </>
                  )}
                  <p className="text-[10px]">
                    {totalRevenue > 0 ? `${(((s?.revenue || 0) / totalRevenue) * 100).toFixed(1)}% of total` : '—'}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {loading && <p className="text-xs text-[#8E85A0]">Loading…</p>}
      <p className="text-[10px] text-[#8E85A0] italic">
        Projections based on scheduled (non-draft) shifts × service billing rates (15-minute units). Rates editable in Settings → Billing.
      </p>
    </div>
  );
}
